import type { Maybe } from 'graphql/jsutils/Maybe';

import type { AdType } from '../../configs';
import type { TargetingValue } from '../../models';

/**
 * `TargetingScope` describes the read-only "world" in which targeting is being
 * chosen for an entity (an Ad, a Line Item, or a multi-edit selection of either).
 *
 * It is the bundle of cross-cutting flags & lookup data that is threaded through
 * the Targeting render tree (Targeting → TargetingDropdowns → TargetingValuesDropdown
 * and into `useTargetingDropdowns` / `useTargetingValuesDropdown`). It does NOT
 * describe Formik state, list mutators, or transient presentation state.
 *
 * Add new entity-scope flags here so the type is the single source of truth.
 */
export type TargetingScope = Readonly<{
  isAdEntity: boolean;

  /**
   * Country IDs of the parent line item (or, in multi-edit, the union across the
   * parent line items of the selected ads / the selected line items themselves).
   *
   * Used to (a) bound the values shown when the user picks the Country dimension,
   * and (b) decide whether the "Include/Exclude" button should be disabled because
   * the user is trying to set the same set of countries that's already in scope.
   */
  lineItemCountryIds?: string[];

  /**
   * Country IDs used to filter the **values** dropdown for non-Country dimensions.
   *
   * In single-entity flows this is typically a subset of `lineItemCountryIds`
   * (e.g. for Ads it is derived from `getCountriesTargetFromAdTargetingSubmission`
   * over the ad's initial targeting). In multi-edit it is the union of each
   * entity's own country target (`countriesTarget` for Ads, `countries` for
   * Line Items) — see `getCountryIdsForFilteringValuesUnion`.
   */
  countryIdsForFilteringValues?: string[];

  /**
   * Publishers in scope for the parent line item (or, in multi-edit, the union of
   * publishers across the entities being edited).
   *
   * Used to (a) filter the Dimension dropdown — the Publisher dimension is hidden
   * if there are no/one supported publisher options — and (b) decide whether the
   * Include/Exclude button is disabled when the Publisher dimension is selected.
   */
  lineItemPublishers?: TargetingValue[];

  /**
   * Publishers used to filter the **values** dropdown when the user picks the
   * Publisher dimension, and passed to `useTargetingValuesDropdown` so that the
   * server-side targeting-values fetch is also filtered.
   *
   * In single-entity flows this is typically equal to `lineItemPublishers`. In
   * multi-edit it is the union of each entity's own publishers — see
   * `getPublishersForFilteringValuesUnion`.
   */
  publishersForFilteringValues?: TargetingValue[];

  adTypes?: Maybe<AdType[]>;

  /**
   * Permits editing the Country dimension when there's a real choice to make
   * (e.g. multiple parent-lineItem countries, or an ad with mismatched country
   * target relative to its line item).
   */
  allowEditCountryTarget?: boolean;

  /**
   * In multi-edit, true when the entities being edited do NOT all share the same
   * country target. When true, categories whose values are inherently scoped to a
   * single country/market (currently "Geo" and "Demo Segments") are dimmed in the
   * Category dropdown with a tooltip explaining why.
   *
   * Single-entity callers should leave this `undefined` / `false`.
   */
  entitiesHaveMismatchedCountries?: boolean;
}>;

export const MISMATCHED_COUNTRIES_CATEGORY_TOOLTIP =
  'Cannot apply targeting category on lines targeting different countries.';

/**
 * Shared `react-tooltip` `id` that anchors every dimmed Category option to
 * the single `ReactTooltip` instance mounted inside the Category dropdown's
 * menu. Each option's `data-for` attribute must match this id, otherwise
 * `react-tooltip` has nothing to render against and the tooltip silently
 * never appears (the failure mode that motivated wiring this up).
 */
export const MISMATCHED_COUNTRIES_CATEGORY_TOOLTIP_ID = 'mismatched-countries-category-tooltip';

/**
 * Dimmed when `TargetingScope.entitiesHaveMismatchedCountries` is true. Matched
 * against `DropdownOption.label` (which carries the category's `displayName`).
 */
export const COUNTRY_SCOPED_CATEGORY_LABELS: ReadonlyArray<string> = ['Geo', 'Demo Segments'];
